/**
 * Hero da home — réplica fiel do `<section class="hero">` de
 * `/home/zielinski/Downloads/AIDA-Formularios-Preview.html`.
 *
 * Mantém a arte orbital da direita (3 órbitas + núcleo + 3 nós + legenda),
 * o gradiente radial do fundo, a reticência do h1 e a escala de tipografia.
 * O botão "Escolher formulário" foi removido conforme pedido.
 *
 * O lockup de marca no topo replica a `.topbar .brand` do HTML de origem
 * (o original posiciona o logo na topbar fixa; aqui ele vai no topo do hero):
 *   .brand         gap:20px, align-items:center
 *   .brand img     width:137px
 *   .brand-divider background:#ddd5dd; width:1px; height:25px
 *   .brand b       letter-spacing:3px; font-size:24px;
 *                  gradiente #b9005b -> #ec4097 com background-clip:text
 *
 * Referência de estilo (do CSS inline do HTML de origem):
 *   .hero       background: radial-gradient(at 86% 20%,#f8d5e7,#0000 58%),
 *                          linear-gradient(110deg,#fff,#fcf5fa);
 *               border:1px solid #eee0e8; border-radius:14px;
 *               justify-content:space-between; align-items:center;
 *               min-height:268px; padding:32px 36px; display:flex;
 *   .hero-art   width:300px; min-height:220px; position:relative;
 *   .orbit      border:1px solid #e2007417; border-radius:50%; centrado;
 *   .orbit-one  145px   .orbit-two 225px   .orbit-three 310px (dashed)
 *   .art-core   82px, gradiente #ef368f->#cc0067, radius 23px, rotate(-8deg)
 *   .art-node   48px, branco translúcido, borda #f4d2e5
 *   .art-caption "DADOS · PROCESSOS · PESSOAS" 8px, letter-spacing 1.5px
 */
import { useId } from 'react';

export interface AidaHeroProps {
  eyebrow?: string;
  title?: React.ReactNode;
  highlight?: string;
  description?: React.ReactNode;
  caption?: string;
  className?: string;
}

/**
 * Ícones extraídos do bundle de
 * `/home/zielinski/Downloads/AIDA-Formularios-Preview.html`:
 *   art-core -> lucide `workflow` (var minificada `Ee`, size 40)
 *   n1       -> lucide `database` (var `de`, size 22)
 *   n2       -> lucide `server`   (var `Ce`, size 22)
 *   n3       -> lucide `book-open`(var `ae`, size 22)
 * Todos usam viewBox 24x24 com traço `currentColor`, como o lucide original.
 */
const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/** art-core — lucide `workflow`. */
function WorkflowIcon({ size = 40 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" {...ICON_STROKE}>
      <rect width="8" height="8" x="3" y="3" rx="2" />
      <path d="M7 11v4a2 2 0 0 0 2 2h4" />
      <rect width="8" height="8" x="13" y="13" rx="2" />
    </svg>
  );
}

/** n1 — lucide `database`. */
function DatabaseIcon({ size = 22 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" {...ICON_STROKE}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
      <path d="M3 12A9 3 0 0 0 21 12" />
    </svg>
  );
}

/** n2 — lucide `server`. */
function ServerIcon({ size = 22 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" {...ICON_STROKE}>
      <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
      <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
      <line x1="6" x2="6.01" y1="6" y2="6" />
      <line x1="6" x2="6.01" y1="18" y2="18" />
    </svg>
  );
}

/** n3 — lucide `book-open`. */
function BookOpenIcon({ size = 22 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" {...ICON_STROKE}>
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </svg>
  );
}

/** Nós decorativos da arte orbital. */
function ArtNode({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <span className={className} aria-hidden="true">
      {children}
    </span>
  );
}



export default function AidaHero({
  eyebrow = 'CONHECIMENTO QUE CONECTA A OPERAÇÃO',
  title = 'Vamos mapear',
  highlight = 'o que faz tudo funcionar.',
  description = (
    <>
      Centralize informações, processos e evidências.
      <br />
      Sua contribuição é o ponto de partida para uma operação melhor.
    </>
  ),
  caption = 'DADOS · PROCESSOS · PESSOAS',
  className = '',
}: AidaHeroProps) {
  const clipId = useId();

  return (
    <section
      className={`aida-hero ${className}`}
      style={
        {
          // Gradiente do original: radial magenta no canto superior direito
          // sobre um linear branco -> rosado, muito sutil. Vem de `--aida-hero-bg`
          // para acompanhar o tema (a classe não vence o inline).
          backgroundImage: 'var(--aida-hero-bg)',
          // Coluna para o lockup de marca ficar acima do conteúdo.
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        } as React.CSSProperties
      }
    >
      {/* Lockup de marca — `.brand` da topbar do HTML de origem. */}
      <div
        className="aida-hero-brand"
        style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 0 }}
      >
        <img
          src="/assets/t-systems.svg"
          alt="T-SYSTEMS"
          width={137}
          style={{ display: 'block', height: 'auto' }}
        />
        <span aria-hidden="true" style={{ background: 'var(--aida-hero-divider)', width: 1, height: 25, flexShrink: 0 }} />
        <b
          style={{
            letterSpacing: 3,
            fontSize: 24,
            fontWeight: 600,
            lineHeight: 1,
            backgroundImage: 'linear-gradient(100deg, #b9005b, #ec4097)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          AIDA
        </b>
      </div>

      <div
        className="aida-hero-row"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 24,
          flex: 1,
          minWidth: 0,
          paddingTop: 0,
        }}
      >
        <div className="aida-hero-copy">
          <div className="aida-hero-eyebrow">{eyebrow}</div>

          <h1 className="aida-hero-title">
            {title}
            <br />
            <span>{highlight}</span>
          </h1>

          <p className="aida-hero-desc">{description}</p>
        </div>

        <div
          className="aida-hero-art"
          aria-hidden="true"
          style={{
            width: 300,
            minHeight: 220,
            marginRight: 20,
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
            }}
          >
            <svg
              style={{ position: 'absolute', width: 0, height: 0 }}
              aria-hidden="true"
            >
              <defs>
                <clipPath id={clipId}>
                  <circle cx="50%" cy="50%" r="50%" />
                </clipPath>
              </defs>
            </svg>

            <div
              className="aida-orbit aida-orbit-1"
              style={{ border: '1px solid var(--aida-hero-orbit)' }}
            />
            <div
              className="aida-orbit aida-orbit-2"
              style={{ border: '1px solid var(--aida-hero-orbit)' }}
            />
            <div
              className="aida-orbit aida-orbit-3"
              style={{ border: '1px dashed var(--aida-hero-orbit-dashed)' }}
            />

            <div
              className="aida-art-core"
              style={{
                backgroundImage: 'linear-gradient(140deg, #ef368f, #cc0067)',
                color: '#ffffff',
              }}
            >
              <WorkflowIcon size={40} />
            </div>

            <span
              className="aida-art-node aida-node-1"
              aria-hidden="true"
              style={{
                color: 'var(--pink)',
                background: 'var(--aida-hero-node-bg)',
                border: '1px solid var(--aida-hero-node-border)',
              }}
            >
              <DatabaseIcon size={22} />
            </span>
            <span
              className="aida-art-node aida-node-2"
              aria-hidden="true"
              style={{
                color: 'var(--pink)',
                background: 'var(--aida-hero-node-bg)',
                border: '1px solid var(--aida-hero-node-border)',
              }}
            >
              <ServerIcon size={22} />
            </span>
            <span
              className="aida-art-node aida-node-3"
              aria-hidden="true"
              style={{
                color: 'var(--pink)',
                background: 'var(--aida-hero-node-bg)',
                border: '1px solid var(--aida-hero-node-border)',
              }}
            >
              <BookOpenIcon size={22} />
            </span>

            <span
              className="aida-art-caption"
              style={{ color: 'var(--aida-hero-caption)' }}
            >
              {caption}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}